import React from 'react';
import Layout from '../components/Layout';
import { Badge } from '../components/ui/badge';
import { MessageCircle, Info, Bell, AlertCircle } from 'lucide-react';

const WhatsAppPage = ({ user, onLogout }) => {
  const templates = [
    {
      id: 'appointment_reminder_simple',
      name: '24-Hour Reminder (Time-based)',
      icon: Bell,
      color: 'text-amber-600',
      bgColor: 'bg-amber-50',
      borderColor: 'border-amber-200',
      trigger: 'Automatically sent 24 hours before time-based appointments (9:00 AM IST daily)',
      params: ['patient_name', 'date', 'time', 'service'],
      example: 'Hi {{1}}, this is a reminder that you have an appointment on {{2}} at {{3}} for {{4}}.\n\nIn case you want to reschedule, please call us at +917710117777.\n\nWe look forward to seeing you!',
      guidelines: [
        'Template type: Simple text message',
        'Includes specific appointment time',
        'Used for time-based appointments only',
        'Includes rescheduling call number +917710117777',
        'Parameters: {{1}}=name, {{2}}=date, {{3}}=time, {{4}}=service'
      ]
    },
    {
      id: 'appointment_reminder_queue',
      name: '24-Hour Reminder (Queue-based)',
      icon: Bell,
      color: 'text-orange-600',
      bgColor: 'bg-orange-50',
      borderColor: 'border-orange-200',
      trigger: 'Automatically sent 24 hours before queue-based appointments (9:00 AM IST daily)',
      params: ['patient_name', 'date', 'service'],
      example: 'Hi {{1}}, this is a reminder that you have an appointment on {{2}} for {{3}}.\n\nIn case you want to reschedule, please call us at +917710117777.\n\nWe look forward to seeing you!',
      guidelines: [
        'Template type: Simple text message',
        'No time mentioned (queue-based)',
        'Used for queue-based appointments only',
        'Includes rescheduling call number +917710117777',
        'Parameters: {{1}}=name, {{2}}=date, {{3}}=service'
      ]
    },
    {
      id: 'noshow_reminder',
      name: 'No-Show Follow-up',
      icon: AlertCircle,
      color: 'text-red-600',
      bgColor: 'bg-red-50',
      borderColor: 'border-red-200',
      trigger: 'Sent automatically when a patient is marked No-Show, then follow-ups on Day 7, 21, and 45 if they haven\'t rebooked',
      params: ['patient_name', 'date', 'service', 'phone'],
      example: 'Hi {{1}}, we noticed you missed your appointment on {{2}} for {{3}}. We\'d love to help you reschedule. Please call us at +917710117777 to rebook.',
      guidelines: [
        'Template type: Simple text message',
        'Day 0: Sent immediately when No-Show is created',
        'Day 7: Resent if patient has no new appointment',
        'Day 21: Resent if still no appointment',
        'Day 45: Final reminder if still no appointment',
        'Sequence stops automatically if patient rebooks any service',
        'Includes rescheduling call number +917710117777',
        'Parameters: {{1}}=name, {{2}}=date, {{3}}=service, {{4}}=clinic phone'
      ]
    }
  ];

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="p-6 max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <div className="flex items-center gap-3 mb-2">
            <MessageCircle className="w-8 h-8 text-green-600" />
            <h1 className="text-3xl font-bold text-gray-900">WhatsApp Message Templates</h1>
          </div>
          <p className="text-sm text-gray-500">Guidelines for automated WhatsApp messages sent via Interakt</p>
        </div>

        {/* Info Banner */}
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-6">
          <div className="flex items-start gap-3">
            <Info className="w-5 h-5 text-blue-600 mt-0.5" />
            <div>
              <h3 className="font-semibold text-blue-900 mb-1">Automatic Appointment Reminders</h3>
              <p className="text-sm text-blue-800">
                These reminders are sent automatically 24 hours before scheduled appointments (at 9:00 AM IST daily).
                Time-based appointments include the specific time, while queue-based appointments only mention the date.
              </p>
            </div>
          </div>
        </div>

        {/* Templates Grid */}
        <div className="grid gap-6">
          {templates.map((template) => {
            const Icon = template.icon;
            return (
              <div
                key={template.id}
                className={`bg-white border ${template.borderColor} rounded-xl p-6 hover:shadow-md transition-shadow`}
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className={`${template.bgColor} p-3 rounded-lg`}>
                      <Icon className={`w-6 h-6 ${template.color}`} />
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-gray-900">{template.name}</h3>
                      <p className="text-sm text-gray-600 mt-1">{template.trigger}</p>
                    </div>
                  </div>
                  <Badge className={template.id === 'location_directions' ? 'bg-yellow-100 text-yellow-800' : 'bg-green-100 text-green-800'}>
                    {template.id === 'location_directions' ? 'Manual' : 'Automatic'}
                  </Badge>
                </div>

                {/* Template ID - IMPORTANT */}
                <div className="mb-4 p-3 bg-gradient-to-r from-purple-50 to-pink-50 border-l-4 border-purple-500 rounded-lg">
                  <div className="flex items-center justify-between">
                    <div>
                      <h4 className="text-xs font-semibold text-purple-700 uppercase mb-1">Interakt Template ID/Name</h4>
                      <code className="text-base font-bold text-purple-900 font-mono bg-white px-3 py-1 rounded border border-purple-200">
                        {template.id}
                      </code>
                    </div>
                    <span className="text-xs text-purple-600 font-semibold">⚠️ USE EXACTLY</span>
                  </div>
                </div>

                {/* Parameters */}
                <div className="mb-4">
                  <h4 className="text-sm font-semibold text-gray-700 mb-2">Template Parameters:</h4>
                  <div className="flex flex-wrap gap-2">
                    {template.params.map(param => (
                      <code key={param} className="px-2 py-1 bg-gray-100 text-gray-800 rounded text-xs font-mono">
                        {`{{${param}}}`}
                      </code>
                    ))}
                  </div>
                </div>

                {/* Example Message */}
                <div className="mb-4">
                  <h4 className="text-sm font-semibold text-gray-700 mb-2">Example Message:</h4>
                  <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
                    <p className="text-sm text-gray-800 whitespace-pre-wrap">{template.example}</p>
                  </div>
                </div>

                {/* Guidelines */}
                <div>
                  <h4 className="text-sm font-semibold text-gray-700 mb-2">Template Guidelines:</h4>
                  <ul className="space-y-1">
                    {template.guidelines.map((guideline, index) => (
                      <li key={index} className="flex items-start gap-2 text-sm text-gray-700">
                        <span className="text-purple-600 mt-0.5">•</span>
                        <span>{guideline}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            );
          })}
        </div>

        {/* Configuration Note */}
        <div className="mt-8 bg-gradient-to-br from-purple-50 to-pink-50 border border-purple-200 rounded-xl p-6">
          <h3 className="font-bold text-gray-900 mb-4 text-lg">Interakt Configuration Instructions</h3>
          
          {/* Template IDs Summary */}
          <div className="mb-6 p-4 bg-white rounded-lg border border-purple-300">
            <h4 className="font-bold text-purple-900 mb-3">📋 Template IDs to Create in Interakt:</h4>
            <div className="space-y-2">
              <div className="flex items-center gap-3">
                <code className="px-3 py-1 bg-amber-100 text-amber-900 rounded font-mono text-sm font-semibold border border-amber-300">
                  appointment_reminder_simple
                </code>
                <span className="text-xs text-amber-700 font-medium">→ 24-hour reminder for time-based (with time)</span>
              </div>
              <div className="flex items-center gap-3">
                <code className="px-3 py-1 bg-orange-100 text-orange-900 rounded font-mono text-sm font-semibold border border-orange-300">
                  appointment_reminder_queue
                </code>
                <span className="text-xs text-orange-700 font-medium">→ 24-hour reminder for queue-based (no time)</span>
              </div>
            </div>
          </div>

          {/* Setup Steps */}
          <div className="space-y-3 text-sm text-gray-700">
            <p className="flex items-start gap-2">
              <span className="font-bold text-purple-700 min-w-[24px]">1.</span>
              <span><strong>Create Templates in Interakt:</strong> Use the exact template IDs/names shown above (case-sensitive). The system looks for these exact names when sending messages.</span>
            </p>
            <p className="flex items-start gap-2">
              <span className="font-bold text-purple-700 min-w-[24px]">2.</span>
              <span><strong>Add Parameters:</strong> Configure all parameters (like {`{{name}}`}, {`{{date}}`}, {`{{token}}`}) in each template exactly as shown in the cards above.</span>
            </p>
            <p className="flex items-start gap-2">
              <span className="font-bold text-purple-700 min-w-[24px]">3.</span>
              <span><strong>API Integration:</strong> Ensure your Interakt API key is configured in the backend environment variables.</span>
            </p>
            <p className="flex items-start gap-2">
              <span className="font-bold text-purple-700 min-w-[24px]">4.</span>
              <span><strong>Test Messages:</strong> Send test messages from Interakt dashboard to verify formatting before going live.</span>
            </p>
            <p className="flex items-start gap-2">
              <span className="font-bold text-purple-700 min-w-[24px]">5.</span>
              <span><strong>Approval:</strong> Get all templates approved by WhatsApp through Interakt before the system can send them.</span>
            </p>
          </div>

          {/* Warning */}
          <div className="mt-4 p-3 bg-amber-50 border border-amber-300 rounded-lg">
            <p className="text-xs text-amber-800">
              ⚠️ <strong>Important:</strong> Template IDs must match exactly (including underscores and lowercase). 
              Any mismatch will cause message sending to fail.
            </p>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default WhatsAppPage;
